/**
 * Chat History Popup Component
 * Shows list of previous chat sessions
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getCloseIcon, getTrashIcon } from "../shared/icons";
export class ChatHistoryPopup {
    constructor(onNewChat, onSelectSession, onClose) {
        this.isVisible = false;
        this.onDeleteSession = null;
        this.onNewChat = onNewChat;
        this.onSelectSession = onSelectSession;
        this.onClose = onClose;
        this.element = this.create();
    }
    /**
     * Set the delete session callback
     */
    setOnDeleteSession(callback) {
        this.onDeleteSession = callback;
    }
    /**
     * Create the popup element
     */
    create() {
        const popup = document.createElement("div");
        popup.className = "me-agent-history-popup";
        popup.style.display = "none";
        popup.innerHTML = `
      <div class="me-agent-history-header">
        <h3 class="me-agent-history-title">Chat History</h3>
        <button class="me-agent-history-close" aria-label="Close history">
          ${getCloseIcon({ width: 20, height: 20 })}
        </button>
      </div>
      <div class="me-agent-history-content">
        <button class="me-agent-new-chat-btn">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          New Chat
        </button>
        <div class="me-agent-history-divider"></div>
        <div class="me-agent-history-label">Recent</div>
        <div class="me-agent-history-list"></div>
      </div>
    `;
        this.setupEventListeners(popup);
        return popup;
    }
    /**
     * Setup event listeners
     */
    setupEventListeners(popup) {
        // Close button
        const closeBtn = popup.querySelector(".me-agent-history-close");
        closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.addEventListener("click", () => this.hide());
        // New chat button
        const newChatBtn = popup.querySelector(".me-agent-new-chat-btn");
        newChatBtn === null || newChatBtn === void 0 ? void 0 : newChatBtn.addEventListener("click", () => {
            this.onNewChat();
            this.hide();
        });
        // Click outside to close
        popup.addEventListener("click", (e) => {
            if (e.target === popup) {
                this.hide();
            }
        });
    }
    /**
     * Show the popup with sessions
     */
    show(sessions, currentSessionId) {
        if (sessions) {
            this.renderSessions(sessions, currentSessionId || null);
        }
        else {
            this.showLoading();
        }
        this.element.style.display = "flex";
        this.isVisible = true;
    }
    /**
     * Update sessions after loading
     */
    updateSessions(sessions, currentSessionId) {
        this.renderSessions(sessions, currentSessionId);
    }
    /**
     * Show loading state
     */
    showLoading() {
        const listContainer = this.element.querySelector(".me-agent-history-list");
        if (!listContainer)
            return;
        listContainer.innerHTML = `
      <div class="me-agent-history-loading">
        <div class="me-agent-history-spinner"></div>
        <p>Loading history...</p>
      </div>
    `;
    }
    /**
     * Hide the popup
     */
    hide() {
        this.element.style.display = "none";
        this.isVisible = false;
        this.onClose();
    }
    /**
     * Render session list
     */
    renderSessions(sessions, currentSessionId) {
        const listContainer = this.element.querySelector(".me-agent-history-list");
        if (!listContainer)
            return;
        if (sessions.length === 0) {
            listContainer.innerHTML = `
        <div class="me-agent-history-empty">
          <p>No chat history yet</p>
        </div>
      `;
            return;
        }
        listContainer.innerHTML = sessions
            .map((session) => {
            const isActive = session.extracted_id === currentSessionId;
            // Use the title field if available, otherwise fallback to extracting from raw_value
            const preview = session.title || this.extractPreview(session.raw_value);
            return `
          <div class="me-agent-history-item ${isActive ? "active" : ""}" data-session-id="${session.extracted_id}">
            <div class="me-agent-history-item-content">
              <div class="me-agent-history-item-preview">${preview}</div>
            </div>
            <button class="me-agent-history-item-delete" data-session-id="${session.extracted_id}" aria-label="Delete chat">
              ${getTrashIcon({ width: 16, height: 16 })}
            </button>
          </div>
        `;
        })
            .join("");
        // Attach click listeners to session items
        const items = listContainer.querySelectorAll(".me-agent-history-item");
        items.forEach((item) => {
            const sessionId = item.getAttribute("data-session-id");
            // Click on item content to select session
            const content = item.querySelector(".me-agent-history-item-content");
            content === null || content === void 0 ? void 0 : content.addEventListener("click", () => {
                if (sessionId) {
                    this.onSelectSession(sessionId);
                    this.hide();
                }
            });
            // Click on delete button to delete session
            const deleteBtn = item.querySelector(".me-agent-history-item-delete");
            deleteBtn === null || deleteBtn === void 0 ? void 0 : deleteBtn.addEventListener("click", (e) => __awaiter(this, void 0, void 0, function* () {
                e.stopPropagation(); // Prevent triggering the item click
                if (sessionId && this.onDeleteSession) {
                    // Show confirmation
                    if (confirm("Are you sure you want to delete this chat?")) {
                        try {
                            yield this.onDeleteSession(sessionId);
                            // Remove the item from the UI
                            item.remove();
                            // If no more items, show empty state
                            const remainingItems = listContainer.querySelectorAll(".me-agent-history-item");
                            if (remainingItems.length === 0) {
                                listContainer.innerHTML = `
                  <div class="me-agent-history-empty">
                    <p>No chat history yet</p>
                  </div>
                `;
                            }
                        }
                        catch (error) {
                            console.error("Failed to delete session:", error);
                            alert("Failed to delete chat. Please try again.");
                        }
                    }
                }
            }));
        });
    }
    /**
     * Extract preview text from session raw_value
     */
    extractPreview(rawValue) {
        try {
            // Try to extract offer_name from state
            const offerNameMatch = rawValue.match(/'offer_name':\s*'([^']+)'/);
            if (offerNameMatch) {
                return offerNameMatch[1];
            }
            // Try to extract recent_searches
            const searchMatch = rawValue.match(/'recent_searches':\s*\[([^\]]+)\]/);
            if (searchMatch) {
                const search = searchMatch[1].replace(/'/g, "").trim();
                return search || "Chat session";
            }
            return "Chat session";
        }
        catch (_a) {
            return "Chat session";
        }
    }
    /**
     * Get the popup element
     */
    getElement() {
        return this.element;
    }
    /**
     * Check if popup is visible
     */
    isOpen() {
        return this.isVisible;
    }
}
